import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ManagementMomService } from '../management-mom.service';
import { ManagementMOM } from '../counsellors-mom.model';
import { AddMomManagementComponent } from './add-mom-management/add-mom-management.component';
import {PermissionType} from "../../../../../components/employee/PermissionType.enum";
import {LogService} from "../../../../../services/log.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

/**
 * Component for listing and managing Management MOM sessions
 */
@Component({
  selector: 'app-mom-management',
  templateUrl: './mom-management.component.html',
  styleUrls: ['./mom-management.component.scss']
})
export class MomManagementComponent implements OnInit, OnDestroy {

  private _destroyed$ = new Subject<void>();

  displayedColumns: string[] = ['date',  'participants', 'remark', 'actions'];
  dataSource = new MatTableDataSource<ManagementMOM>([]);
  isLoading = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('viewDialog') viewDialog!: TemplateRef<any>;
  selectedSession: ManagementMOM | null = null;

  constructor(
    private managementMomService: ManagementMomService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private mLogService: LogService,
  ) { }

  ngOnInit(): void {
    this.loadSessions();
  }

  /**
   * Load all management MOM sessions
   */
  loadSessions() {
    this.isLoading = true;
    this.managementMomService.getAll().pipe(takeUntil(this._destroyed$)).subscribe(
      (sessions: any[]) => {
        this.dataSource.data = sessions.sort((a: any, b: any) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
        console.log(sessions);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Error loading sessions:', error);
        this.isLoading = false;
      }
    );
  }
  openViewDialog(session: ManagementMOM) {
    this.selectedSession = session;

    this.dialog.open(this.viewDialog, {
      width: '800px',
      disableClose: true
    });
  }

  closeView() {
    this.dialog.closeAll();
  }

  /** Firestore-safe date converter */
  getDate(val: any): Date | null {
    if (!val) return null;

    if (val.seconds) {
      return new Date(val.seconds * 1000);
    }

    if (val.toDate) {
      return val.toDate();
    }

    return new Date(val);
  }


  /**
   * Open dialog to add new session
   */
  openAddDialog() {
    const dialogRef = this.dialog.open(AddMomManagementComponent, {
      width: '95%',
      maxWidth: '1400px',
      maxHeight: '90vh',
      data: null
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'saved' || result === 'updated') {
        this.loadSessions();
      }
    });
  }

  /**
   * Open dialog to edit existing session
   */
  openEditDialog(session: ManagementMOM) {
    const dialogRef = this.dialog.open(AddMomManagementComponent, {
      width: '95%',
      maxWidth: '1400px',
      data: session
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'saved' || result === 'updated') {
        this.loadSessions();
      }
    });
  }

  /**
   * Delete a session
   */
  deleteSession(session: ManagementMOM) {
    if (confirm('Are you sure you want to delete this session?')) {
      this.managementMomService.deleteRow(session.id!).then(() => {
        this.snackBar.open('Session deleted successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
        const activity = {
          date: new Date().getTime(),
          section: 'Management MOM',
          action: 'Deleted',
          description: `Management MOM details deleted by user`,
          currentIp: localStorage.getItem('currentip')!
        };
        this.mLogService.addLog(activity);
        this.loadSessions();
      }).catch((error: any) => {
        this.snackBar.open('Error deleting session: ' + error.message, 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      });
    }
  }

  /**
   * Apply filter to the table
   */
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: any): string {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  }

  /**
   * Get participant names as comma-separated string
   */
  getParticipantNames(participants: string[]): string {
    if (!participants || participants.length === 0) return '';
    return participants.join(', ');
  }

  /**
   * Get count of session items
   */
  getSessionCount(session: ManagementMOM): number {
    return session.sessions?.length || 0;
  }

  protected readonly PermissionType = PermissionType;

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}

